import CloseIcon from "@mui/icons-material/Close"
import ScienceIcon from "@mui/icons-material/Science"
import LoadingButton from "@mui/lab/LoadingButton"
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import {
  ALL_AI_MODELS,
  supportsCustomTemperature,
  type AiModels,
  type AiPromptModelSettings
} from "@pages/main/scripts/prompts/prompt.models"
import type { BaseResponseSchema } from "@pages/main/scripts/prompts/sub_components/ResponseSchemaBuilder"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import CasesTab from "./CasesTab"
import ResultsTab from "./ResultsTab"
import PromptTab from "./PromptTab"
import RunTab from "./RunTab"
import { makeId, runSuite, type PromptDraft } from "./runner"
import {
  clearRuns,
  DEFAULT_MAX_TOKENS,
  deleteRun,
  loadRuns,
  loadSettings,
  loadSuite,
  saveRun,
  saveSettings,
  saveSuite
} from "./storage"
import type {
  PlaygroundRun,
  PlaygroundSettings,
  PromptTestSuite,
  RunProgress
} from "./types"

const TAB = { RUN: 0, PROMPT: 1, CASES: 2, RESULTS: 3 }

interface PlaygroundDrawerProps {
  open: boolean
  onClose: () => void
  promptName: string
  systemPrompt: string
  model: AiModels
  modelSettings?: AiPromptModelSettings | null
  responseSchema?: BaseResponseSchema | null
  isDirty: boolean
  onSystemPromptChange?: (value: string) => void
  onSave?: () => void
  canSave?: boolean
  saving?: boolean
}

export default function PlaygroundDrawer(props: PlaygroundDrawerProps) {
  const {
    open,
    onClose,
    promptName,
    systemPrompt,
    model,
    modelSettings,
    responseSchema,
    isDirty,
    onSystemPromptChange,
    onSave,
    canSave,
    saving
  } = props

  const storageKey = promptName || "__unsaved__"
  const [tab, setTab] = useState(0)
  const [settings, setSettings] = useState<PlaygroundSettings>(() =>
    loadSettings(storageKey)
  )
  const [suite, setSuite] = useState<PromptTestSuite>(() =>
    loadSuite(storageKey)
  )
  const [runs, setRuns] = useState<PlaygroundRun[]>(() => loadRuns(storageKey))
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState<RunProgress | null>(null)
  const stopRef = useRef(false)

  useEffect(() => {
    setSettings(loadSettings(storageKey))
    setSuite(loadSuite(storageKey))
    setRuns(loadRuns(storageKey))
    setSelectedRunId(null)
  }, [storageKey])

  const patchSettings = useCallback(
    (patch: Partial<PlaygroundSettings>) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch }
        saveSettings(storageKey, next)
        return next
      })
    },
    [storageKey]
  )

  const updateSuite = useCallback(
    (next: PromptTestSuite) => {
      setSuite(next)
      saveSuite(storageKey, next)
    },
    [storageKey]
  )

  const effectiveModel = settings.modelOverride || model
  const temperatureLocked = !supportsCustomTemperature(effectiveModel ?? "")

  const draft: PromptDraft = useMemo(
    () => ({
      name: promptName,
      systemPrompt,
      model: effectiveModel,
      modelSettings,
      responseSchema
    }),
    [promptName, systemPrompt, effectiveModel, modelSettings, responseSchema]
  )

  async function handleRunCases(caseIds?: string[]) {
    const cases = caseIds
      ? suite.cases.filter((c) => caseIds.includes(c.id))
      : suite.cases
    if (!cases.length) return

    stopRef.current = false
    setRunning(true)
    setProgress({
      done: 0,
      total: cases.length * settings.repeats,
      currentCase: ""
    })

    const run = await runSuite(
      { ...suite, cases },
      {
        draft,
        defaults: suite.defaults,
        baseVariables: settings.variables,
        baseConditions: settings.conditions,
        temperature:
          settings.temperature === ""
            ? undefined
            : Number(settings.temperature),
        maxTokens:
          settings.maxTokens === "" ? undefined : Number(settings.maxTokens),
        timeoutMs:
          settings.timeoutMs === "" ? undefined : Number(settings.timeoutMs),
        repeats: settings.repeats,
        concurrency: settings.concurrency,
        judgeModel: settings.judgeModel || undefined
      },
      {
        onProgress: setProgress,
        shouldStop: () => stopRef.current
      }
    )

    setRuns(saveRun(storageKey, run))
    setSelectedRunId(run.id)
    setRunning(false)
    setProgress(null)
    setTab(TAB.RESULTS)
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={running ? undefined : onClose}
      PaperProps={{
        sx: {
          width: "min(1180px, 94vw)",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}
        flexWrap="wrap"
        useFlexGap
      >
        <ScienceIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>
          {promptName || "Kaydedilmemiş prompt"}
        </Typography>
        {isDirty && (
          <Tooltip title="Editördeki kaydedilmemiş metin test edilecek">
            <Chip size="small" color="warning" label="DRAFT" />
          </Tooltip>
        )}
        {responseSchema && (
          <Chip size="small" variant="outlined" label="schema" />
        )}

        <Box flex={1} />

        <Select
          size="small"
          value={settings.modelOverride || model || ""}
          sx={{ minWidth: 190 }}
          onChange={(event) => {
            const picked = event.target.value as AiModels
            patchSettings({ modelOverride: picked === model ? "" : picked })
          }}
        >
          {ALL_AI_MODELS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
              {option === model ? " (formdaki)" : ""}
            </MenuItem>
          ))}
        </Select>
        <Tooltip
          title={
            temperatureLocked
              ? "Bu model yalnızca varsayılan sıcaklığı (1) kabul eder"
              : ""
          }
        >
          <TextField
            size="small"
            label="temp"
            type="number"
            sx={{ width: 86 }}
            disabled={temperatureLocked}
            inputProps={{ step: 0.1, min: 0, max: 2 }}
            value={temperatureLocked ? 1 : settings.temperature}
            onChange={(event) =>
              patchSettings({
                temperature:
                  event.target.value === "" ? "" : Number(event.target.value)
              })
            }
          />
        </Tooltip>
        <TextField
          size="small"
          label="maxTok"
          type="number"
          sx={{ width: 96 }}
          inputProps={{ min: 1, max: DEFAULT_MAX_TOKENS }}
          helperText={`≤ ${DEFAULT_MAX_TOKENS / 1000}k`}
          FormHelperTextProps={{ sx: { m: 0, fontSize: 10 } }}
          value={settings.maxTokens}
          onChange={(event) =>
            patchSettings({
              maxTokens:
                event.target.value === "" ? "" : Number(event.target.value)
            })
          }
        />
        {onSave && (
          <LoadingButton
            size="small"
            variant="outlined"
            loading={saving}
            disabled={!canSave || running}
            onClick={onSave}
          >
            Kaydet
          </LoadingButton>
        )}
        <IconButton size="small" onClick={onClose} disabled={running}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_event, value: number) => setTab(value)}
        sx={{ px: 1.5, borderBottom: 1, borderColor: "divider", minHeight: 40 }}
      >
        <Tab label="Hızlı Test" sx={{ minHeight: 40 }} value={TAB.RUN} />
        <Tab label="Prompt" sx={{ minHeight: 40 }} value={TAB.PROMPT} />
        <Tab
          label={`Hazır Sorular (${suite.cases.length})`}
          sx={{ minHeight: 40 }}
          value={TAB.CASES}
        />
        <Tab
          label={`Sonuçlar (${runs.length})`}
          sx={{ minHeight: 40 }}
          value={TAB.RESULTS}
        />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, p: 1.5 }}>
        {tab === TAB.RUN && (
          <RunTab
            draft={draft}
            settings={settings}
            onSettingsChange={patchSettings}
            onSaveAsCase={(params) =>
              updateSuite({
                ...suite,
                cases: [
                  ...suite.cases,
                  {
                    id: makeId("case"),
                    name: params.query.slice(0, 48) || "Yeni soru",
                    query: params.query,
                    variables: Object.keys(params.variables).length
                      ? params.variables
                      : undefined,
                    conditions: Object.keys(params.conditions).length
                      ? params.conditions
                      : undefined,
                    previousMessages: params.previousMessages.length
                      ? params.previousMessages
                      : undefined
                  }
                ]
              })
            }
          />
        )}
        {tab === TAB.PROMPT && (
          <PromptTab
            systemPrompt={systemPrompt}
            onChange={(value) => onSystemPromptChange?.(value)}
            isDirty={isDirty}
          />
        )}
        {tab === TAB.CASES && (
          <CasesTab
            draft={draft}
            suite={suite}
            onSuiteChange={updateSuite}
            settings={settings}
            onSettingsChange={patchSettings}
            running={running}
            progress={progress}
            onRunCases={handleRunCases}
            onStop={() => {
              stopRef.current = true
            }}
          />
        )}
        {tab === TAB.RESULTS && (
          <ResultsTab
            runs={runs}
            selectedRunId={selectedRunId}
            onSelectRun={setSelectedRunId}
            onDeleteRun={(runId) => setRuns(deleteRun(storageKey, runId))}
            onClearRuns={() => setRuns(clearRuns(storageKey))}
          />
        )}
      </Box>
    </Drawer>
  )
}
